/**
 * Engagement status badge — promoted from the inline composition that lived in
 * `EngagementHub/Header.tsx`. Takes an Engagement and renders a pill with a
 * leading colored dot, label, and tone-derived border/background.
 *
 * Two sizes:
 *  - `md` (default): subtitle-1 / rounded-md / px-12 py-6 — matches the Hub
 *    Header pill, byte-for-byte with the prior inline StatusBadge.
 *  - `sm`: util-caption / rounded-full / px-8 py-2 — matches the smaller pill
 *    used in list-row contexts (Governance Inbox, Fulfillment Feed). Visually
 *    aligned with the HoursTab `StatusPill` so list-row visual language stays
 *    consistent with the in-Hub Hours-status pill.
 *
 * Tone is derived from the engagement via `statusBadgeTone`; label via
 * `statusBadgeText`. Both helpers stay in `EngagementHub/status.ts` because
 * they're used independently of this badge (e.g., for sortable columns on
 * the Fulfillment Feed where we want the tone but not the visual chrome).
 */
import type { Engagement } from '../../types';
import type { ViewerRole } from '../EngagementHub/roles';
import { type StatusTone, statusBadgeText, statusBadgeTone } from '../EngagementHub/status';

type Size = 'sm' | 'md';

export interface EngagementStatusBadgeProps {
  engagement: Engagement;
  size?: Size;
  /**
   * Optional — enables the §9.6 viewer-aware copy (expert sees "Awaiting
   * customer accept"). List-row call sites omit it and keep current copy.
   */
  viewerRole?: ViewerRole;
  /** Optional className passthrough for layout positioning. */
  className?: string;
}

export const TONE_CLASSES: Record<
  StatusTone,
  { bg: string; text: string; border: string; dot: string }
> = {
  active: {
    bg: 'bg-brand-light',
    text: 'text-on-surface',
    border: 'border-brand-primary-faint',
    dot: 'bg-brand-primary',
  },
  attention: {
    bg: 'bg-yellow-50 dark:bg-yellow-900/20',
    text: 'text-yellow-900 dark:text-yellow-200',
    border: 'border-yellow-700/40',
    dot: 'bg-yellow-500',
  },
  progress: {
    bg: 'bg-purple-50 dark:bg-purple-900/20',
    text: 'text-purple-900 dark:text-purple-200',
    border: 'border-purple-700/40',
    dot: 'bg-purple-500',
  },
  muted: {
    bg: 'bg-surface-secondary',
    text: 'text-on-surface-muted',
    border: 'border-surface-border',
    dot: 'bg-on-surface-muted',
  },
  neutral: {
    bg: 'bg-surface-elevated',
    text: 'text-on-surface',
    border: 'border-surface-border',
    dot: 'bg-on-surface-muted',
  },
};

const SIZE_CLASSES: Record<Size, { wrapper: string; dot: string }> = {
  md: {
    wrapper: 'gap-8 rounded-md px-12 py-6 subtitle-1',
    dot: 'h-8 w-8',
  },
  sm: {
    wrapper: 'gap-6 rounded-full px-8 py-2 util-caption',
    dot: 'h-6 w-6',
  },
};

export default function EngagementStatusBadge({
  engagement,
  size = 'md',
  viewerRole,
  className,
}: EngagementStatusBadgeProps): JSX.Element {
  const tone = statusBadgeTone(engagement);
  const label = statusBadgeText(engagement, viewerRole);
  const c = TONE_CLASSES[tone];
  const s = SIZE_CLASSES[size];
  return (
    <span
      className={`inline-flex items-center border ${s.wrapper} ${c.bg} ${c.text} ${c.border}${
        className ? ` ${className}` : ''
      }`}
    >
      <span className={`shrink-0 rounded-full ${s.dot} ${c.dot}`} aria-hidden />
      {label}
    </span>
  );
}
